// TechMenu (round 2, a board composition; pages/board-tech-menu.md): right-click a technician's name on
// the day board, or press it, or press the menu key or Shift+F10 on it: it opens beside the name, to its
// right, moved up only as far as it must to fit (Menu's placement "beside"). On top, their day so far, live
// (TechDayCard); under it, where to go (their timesheet at today, their truck's stock), how to reach
// them (call, text, the office's message thread), and what to do on the board (show their route on the
// map, show only them, hide them for today). Who sees what follows the spec's table: minutes only, never
// pay; the timesheet, a day's fix and Edit hours only for those who correct hours; the truck only with
// inventory access; a CSR sees what they're doing now, nothing more. Their week is gone (QA-751): the
// Week page was deleted (Amir, 26 Oct), and the timesheet already shows their pay period week by week.
import { Menu, type MenuAt, type MenuItem, type MenuSection } from '../Menu/Menu.tsx';
import { TechDayCard, type TechDay, type TechDayStatus } from '../TechDayCard/TechDayCard.tsx';

export type TechMenuRole = 'owner' | 'manager' | 'bookkeeper' | 'dispatcher' | 'csr';

export interface TechMenuViewer {
  role: TechMenuRole;
  /** Inventory access (owners and managers have it; a dispatcher may). */
  inventory?: boolean;
}

/** The spec's who-sees-what table, as a function. */
export function techMenuAccess({ role, inventory }: TechMenuViewer) {
  const csr = role === 'csr';
  return {
    card: csr ? ('now' as const) : ('full' as const),
    problems: !csr,
    timesheet: role === 'owner' || role === 'manager' || role === 'bookkeeper',
    /** Fix a day's problem and Edit hours: the same people as the timesheet (they correct hours). */
    fix: role === 'owner' || role === 'manager' || role === 'bookkeeper',
    editHours: role === 'owner' || role === 'manager' || role === 'bookkeeper',
    truck: role === 'owner' || role === 'manager' || (role === 'dispatcher' && !!inventory),
  };
}

export type TechMenuAction =
  'fix' | 'editHours' | 'timesheet' | 'truck' | 'call' | 'text' | 'message' | 'map' | 'only' | 'hide';

export interface TechMenuProps {
  /** What it opened from (their name; a point is read as a spot beside), or null. */
  at: MenuAt | null;
  onClose: () => void;
  day: TechDay;
  asOf: number;
  status?: TechDayStatus;
  viewer: TechMenuViewer;
  /** Their phone, shown beside Call; without one, Call and Text are off. */
  phone?: string | null;
  /** Where the links go: Hours at their day, Inventory at their truck. */
  links?: { timesheet?: string; truck?: string };
  onAction?: (action: TechMenuAction) => void;
  /**
   * Their time's problem to fix ("Fix Fri Oct 23…"): first in Go to, in ink (the card says it in red).
   * Choosing it closes the menu, then runs onFix (after the menu has handed focus back), which opens
   * the caller's quick edit. When that closes, focusLaneName(laneId) puts focus back on their name.
   */
  fix?: { label: string; onFix: () => void } | null;
  /**
   * "Edit hours…": the quick edit at a day (today, or the last day they worked). Leave it out (don't
   * pass it) when it would open the same day as `fix`.
   */
  onEditHours?: (() => void) | null;
  /** Edit hours' hint: "Today", or the day ("Fri Oct 23"). */
  editHoursHint?: string;
  /** The card's problems in the timesheet's words (TechDayCard's `problems`, problemCardWords). */
  problems?: readonly string[];
  /** Who fixes them (problemsNoteWords): shown only to a viewer who can't fix time. */
  problemsNote?: string | null;
  /**
   * Message shows (the office-to-tech thread); off where the app has no such thread yet, so the menu
   * never offers what it can't do.
   */
  canMessage?: boolean;
  onSelectVisit?: (visitId: string) => void;
  onRetry?: () => void;
  /** More sections after these (their time-off and away requests, BL-159). */
  more?: MenuSection[];
}

/** Go to's items, in order, for this viewer. */
export function techMenuGoTo({
  viewer,
  truck,
  links = {},
  fixLabel,
  editHours = false,
  editHoursHint = 'Today',
}: {
  viewer: TechMenuViewer;
  /** Their truck's name, if they have one. */
  truck?: string | null;
  links?: TechMenuProps['links'];
  /** The fix's label, when there's a problem to fix. */
  fixLabel?: string | null;
  editHours?: boolean;
  editHoursHint?: string;
}): MenuItem[] {
  const can = techMenuAccess(viewer);
  const goTo: MenuItem[] = [];
  if (can.fix && fixLabel) goTo.push({ id: 'fix', label: fixLabel });
  if (can.editHours && editHours) goTo.push({ id: 'editHours', label: 'Edit hours…', hint: editHoursHint });
  if (can.timesheet) goTo.push({ id: 'timesheet', label: 'Timesheet', hint: 'Today', href: links.timesheet });
  if (can.truck && truck) goTo.push({ id: 'truck', label: 'Truck stock', hint: truck, href: links.truck });
  return goTo;
}

export function TechMenu({
  at,
  onClose,
  day,
  asOf,
  status = 'live',
  viewer,
  phone,
  links = {},
  onAction,
  fix,
  onEditHours,
  editHoursHint = 'Today',
  problems,
  problemsNote,
  canMessage = true,
  onSelectVisit,
  onRetry,
  more = [],
}: TechMenuProps) {
  const can = techMenuAccess(viewer);
  const first = day.name.split(/\s+/)[0] || day.name;
  const doFix = can.fix ? fix : null;
  const editHours = can.editHours ? onEditHours : null;
  const goTo = techMenuGoTo({
    viewer,
    truck: day.truck?.name ?? null,
    links,
    fixLabel: doFix?.label ?? null,
    editHours: !!editHours,
    editHoursHint,
  });
  const noPhone = !phone;
  const sections: MenuSection[] = [
    { label: 'Go to', items: goTo },
    {
      label: 'Contact',
      items: [
        {
          id: 'call',
          label: 'Call',
          hint: phone ?? undefined,
          reason: noPhone ? 'No number' : undefined,
          isDisabled: noPhone,
        },
        { id: 'text', label: 'Text', reason: noPhone ? 'No number' : undefined, isDisabled: noPhone },
        ...(canMessage ? [{ id: 'message', label: 'Message' }] : []),
      ],
    },
    {
      label: 'On the board',
      items: [
        { id: 'map', label: 'Show on map' },
        { id: 'only', label: `Show only ${first}` },
        { id: 'hide', label: `Hide ${first} for today` },
      ],
    },
    ...more,
  ];
  return (
    <Menu
      at={at}
      onClose={onClose}
      label={`${day.name}’s menu`}
      // beside their name, never over it or the site header; taller than the screen, it scrolls inside
      placement="beside"
      head={
        <TechDayCard
          day={day}
          asOf={asOf}
          status={status}
          detail={can.card}
          showProblems={can.problems}
          problems={problems}
          problemsNote={can.fix ? null : problemsNote}
          onRetry={onRetry}
          onSelectVisit={
            onSelectVisit
              ? (id) => {
                  onSelectVisit(id);
                  onClose();
                }
              : undefined
          }
        />
      }
      sections={sections}
      onAction={(id) => {
        // a dialog opened from here waits until the menu has closed and handed focus back
        const later = (run: () => void) => requestAnimationFrame(() => requestAnimationFrame(run));
        if (id === 'fix' && doFix) later(doFix.onFix);
        if (id === 'editHours' && editHours) later(editHours);
        onAction?.(id as TechMenuAction);
      }}
    />
  );
}
